import React, { useState, useRef, useEffect } from 'react';
import {
  Container, Paper, Typography, TextField, Button, Box,
  List, ListItem, ListItemText, Avatar, CircularProgress,
  Divider, useTheme
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import axios from 'axios';


const ChatBot = () => {
  const theme = useTheme();
  const [messages, setMessages] = useState([
    { role: 'system', content: 'I am an AI assistant that helps with academic questions and test preparation.' },
    { role: 'assistant', content: 'Hello! I\'m your AI study assistant. Ask me any questions about your studies or tests, and I\'ll do my best to help you!' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  const sendMessage = async () => {
    if (!input.trim()) return;

    // Add user message to chat
    const userMessage = { role: 'user', content: input }; // Added role: 'user'
    setMessages(prevMessages => [...prevMessages, userMessage]);
    setInput('');
    setLoading(true);

    try {
      // Filter out system messages for the API call
      const messagesToSend = [...messages, userMessage].filter(
        msg => msg.role === 'user' || msg.role === 'assistant'
      );

      // Call the backend API
      const response = await axios.post('/api/ai/chat', {
        messages: messagesToSend
      });

      // Extract AI response
      // Assuming the API response structure is { content: "..." } as per the screenshot
      const aiMessageContent = response.data.content; 
      if (aiMessageContent) {
        setMessages(prev => [...prev, { role: 'assistant', content: aiMessageContent }]);
      } else {
        // Fallback if the expected content is not there, or handle more specific structures if known
        // For example, if it's still under choices[0].message for some cases:
        const detailedMessage = response.data.choices && response.data.choices[0] && response.data.choices[0].message;
        if (detailedMessage) {
            setMessages(prev => [...prev, { role: 'assistant', content: detailedMessage.content || detailedMessage }]);
        } else {
            throw new Error('AI response format is not recognized.');
        }
      }

    } catch (error) {
      console.error('Error getting AI response:', error);
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant', // Ensure error messages also have a role
          content: 'Sorry, I encountered an error. Please try again later.'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h4" gutterBottom align="center" sx={{ mb: 3, color: 'primary.main' }}>
          AI Study Assistant
        </Typography>
        <Typography variant="body1" gutterBottom align="center" sx={{ mb: 3, color: 'text.secondary' }}>
          Ask me any questions about your studies, exams, or concepts you're struggling with.
        </Typography>
        
        <Box sx={{ height: '400px', overflowY: 'auto', mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <List>
            {messages.filter(msg => msg.role !== 'system').map((message, index) => (
              <ListItem key={index} alignItems="flex-start" sx={{
                justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                mb: 1
              }}>
                {message.role === 'assistant' && (
                  <Avatar sx={{ mr: 1, bgcolor: 'primary.main' }}>
                    <SchoolIcon />
                  </Avatar>
                )}
                
                <Paper 
                  elevation={1} 
                  sx={{
                    p: 2,
                    maxWidth: '80%',
                    bgcolor: message.role === 'user' ? 'primary.light' : 'background.default',
                    color: message.role === 'user' ? (theme.palette.mode === 'light' ? 'text.primary' : 'primary.contrastText') : 'text.primary',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.content}
                  </Typography>
                </Paper>
                
                {message.role === 'user' && (
                  <Avatar sx={{ ml: 1, bgcolor: 'success.main' }}>
                    <PersonIcon />
                  </Avatar>
                )}
              </ListItem>
            ))}
            <div ref={messagesEndRef} />
          </List>
          {loading && (
            <Box display="flex" justifyContent="center" my={2}>
              <CircularProgress size={30} />
            </Box>
          )}
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            variant="outlined"
            placeholder="Type your question here...."
            value={input}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            disabled={loading}
            sx={{ mr: 1 }}
          />
          <Button 
            variant="contained" 
            color="primary" 
            endIcon={<SendIcon />}
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            sx={{ height: '56px' }}
          >
            Send
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ChatBot;
